import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, User, ShoppingBag, Store } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { LOGO_URL } from "@/i18n/translations";
import { getShopStatus } from "@/lib/shop";

const LINKS = [
  { id: "about", key: "about" },
  { id: "services", key: "services" },
  { id: "nagayu", key: "nagayu" },
  { id: "selfwash", key: "selfwash" },
  { id: "gallery", key: "gallery" },
  { id: "contact", key: "contact" },
];

export const Navbar = () => {
  const { lang, changeLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [shopVisible, setShopVisible] = useState(false);
  const { count } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const check = () => getShopStatus().then((st) => setShopVisible(!st.hidden));
    check();
    window.addEventListener("cfm-shop-unlock", check);
    window.addEventListener("focus", check);
    return () => {
      window.removeEventListener("cfm-shop-unlock", check);
      window.removeEventListener("focus", check);
    };
  }, [location.pathname]);

  const goSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/#" + id);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  // Cart shows only inside the boutique area
  const inShop = location.pathname.startsWith("/shop") || location.pathname.startsWith("/cart");

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 glass border-b transition-[box-shadow,border-color] duration-500 ${
        scrolled ? "border-brand/10 shadow-[0_8px_30px_rgba(42,37,34,0.08)]" : "border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <button
          data-testid="logo-home-btn"
          onClick={() => (location.pathname === "/" ? window.scrollTo({ top: 0, behavior: "smooth" }) : navigate("/"))}
          className="flex items-center no-a11y-underline group"
          aria-label="Care For Me home"
        >
          <img src={LOGO_URL} alt="Care For Me" className="h-9 sm:h-11 w-auto transition-transform duration-300 group-hover:scale-105" />
        </button>

        <div className="hidden xl:flex items-center gap-0.5">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => goSection(l.id)}
              className="relative px-3 py-2 text-[13.5px] font-semibold text-brand-ink/75 hover:text-brand transition-colors duration-200 no-a11y-underline group"
            >
              {t.nav[l.key]}
              <span className="absolute left-3 right-3 -bottom-0.5 h-px bg-bamboo scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {shopVisible && inShop && (
            <button
              data-testid="nav-cart-btn"
              onClick={() => { setOpen(false); navigate("/cart"); }}
              className="relative grid place-items-center h-10 w-10 rounded-full text-brand-ink/70 hover:text-brand hover:bg-brand-soft/70 transition-colors no-a11y-underline"
              aria-label={t.shop.cart_title}
            >
              <ShoppingBag className="h-[18px] w-[18px]" />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 grid place-items-center h-[18px] min-w-[18px] px-1 rounded-full bg-brand text-brand-cream text-[10px] font-bold" data-testid="nav-cart-count">
                  {count}
                </span>
              )}
            </button>
          )}

          <button
            data-testid="nav-account-btn"
            onClick={() => { setOpen(false); navigate("/account"); }}
            className="hidden md:grid place-items-center h-10 w-10 rounded-full text-brand-ink/70 hover:text-brand hover:bg-brand-soft/70 transition-colors no-a11y-underline"
            aria-label={t.nav.my_account}
            title={t.nav.my_account}
          >
            <User className="h-[18px] w-[18px]" />
          </button>

          <div className="flex items-center rounded-full border border-brand/15 p-0.5 bg-white/60" role="group" aria-label="Language switcher">
            {["el", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-switch-${l}`}
                onClick={() => changeLang(l)}
                className={`px-2 py-1 rounded-full text-[11px] font-bold uppercase transition-colors duration-200 no-a11y-underline ${
                  lang === l ? "bg-brand text-brand-cream" : "text-brand-ink/60 hover:text-brand"
                }`}
                aria-pressed={lang === l}
              >
                {l}
              </button>
            ))}
          </div>

          {shopVisible && (
            <Button
              data-testid="nav-boutique"
              onClick={() => { setOpen(false); navigate("/shop"); }}
              variant="outline"
              className={`hidden sm:inline-flex rounded-full border-brand/30 font-bold h-10 no-a11y-underline ${
                inShop ? "bg-brand text-brand-cream hover:bg-brand-hover border-brand" : "text-brand hover:bg-brand-soft"
              }`}
            >
              <Store className="h-4 w-4 mr-1.5" />
              {t.nav.boutique}
            </Button>
          )}

          <Button
            data-testid="nav-book-btn"
            onClick={() => goSection("grooming")}
            className="hidden sm:inline-flex rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold px-5 h-10 no-a11y-underline"
          >
            {t.nav.book}
          </Button>

          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen((o) => !o)}
            className="xl:hidden grid place-items-center h-10 w-10 rounded-full border border-brand/20 text-brand-ink"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="xl:hidden glass border-t border-brand/10 px-5 py-4 flex flex-col gap-1 animate-fade-in">
          {[...LINKS, { id: "grooming", key: "grooming" }].map((l) => (
            <button
              key={l.id}
              data-testid={`mobile-nav-${l.id}`}
              onClick={() => goSection(l.id)}
              className="text-left px-4 py-3 rounded-2xl text-base font-semibold text-brand-ink hover:bg-bamboo-soft transition-colors duration-200 no-a11y-underline"
            >
              {t.nav[l.key]}
            </button>
          ))}
          {shopVisible && (
            <button
              data-testid="mobile-nav-boutique"
              onClick={() => { setOpen(false); navigate("/shop"); }}
              className="text-left px-4 py-3 rounded-2xl text-base font-semibold text-brand-ink hover:bg-bamboo-soft transition-colors duration-200 no-a11y-underline flex items-center gap-2"
            >
              <Store className="h-4 w-4 text-brand" /> {t.nav.boutique}
            </button>
          )}
          <button
            data-testid="mobile-nav-account"
            onClick={() => { setOpen(false); navigate("/account"); }}
            className="text-left px-4 py-3 rounded-2xl text-base font-semibold text-brand hover:bg-brand-soft transition-colors duration-200 no-a11y-underline flex items-center gap-2"
          >
            <User className="h-4 w-4" /> {t.nav.my_account}
          </button>
        </div>
      )}
    </header>
  );
};
